import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ExternalLink, MousePointerClick, Store, BookOpen, ShoppingBag, ClipboardPaste, Sparkles, Star, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const REC_EMPTY = { title: "", retailer: "Amazon", affiliate_url: "", image_url: "", amazon_image_link: "", recommendation: "", category: "", price_note: "", status: "draft", sort_order: 0, asin: "", source: "amazon", is_must_have: false };
const GUIDE_EMPTY = { title: "", summary: "", body: "", status: "draft", sort_order: 0 };
const RETAILER_EMPTY = { name: "", website: "", logo_url: "", notes: "", active: false };

function Field({ label, children }) {
  return (
    <div className="mt-4">
      <label className="block text-sm font-bold text-navy uppercase">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick";

export default function AdminAffiliates() {
  const [tab, setTab] = useState("recs");
  const [stats, setStats] = useState(null);

  const [recs, setRecs] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [guides, setGuides] = useState([]);

  const [modal, setModal] = useState(null); // {kind, editing, form}
  const [saving, setSaving] = useState(false);
  const [amzUrl, setAmzUrl] = useState("");
  const [amzImporting, setAmzImporting] = useState(false);
  const [amzOpen, setAmzOpen] = useState(false);

  const loadAll = () => {
    api.get("/admin/affiliates").then((r) => setRecs(r.data)).catch(() => {});
    api.get("/admin/affiliate-retailers").then((r) => setRetailers(r.data)).catch(() => {});
    api.get("/admin/affiliate-guides").then((r) => setGuides(r.data)).catch(() => {});
    api.get("/admin/affiliates-stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const openRec = (r) => setModal({ kind: "rec", editing: r, form: r ? { ...REC_EMPTY, ...r } : REC_EMPTY });
  const openGuide = (g) => setModal({ kind: "guide", editing: g, form: g ? { ...g } : GUIDE_EMPTY });
  const openRetailer = (rt) => setModal({ kind: "retailer", editing: rt, form: rt ? { ...rt } : RETAILER_EMPTY });

  const pasteClipboard = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) setAmzUrl(t.trim());
    } catch {
      toast.message("Paste blocked by browser — long-press the field to paste.");
    }
  };

  const importAmazon = async () => {
    if (!amzUrl.trim() || amzImporting) return;
    setAmzImporting(true);
    try {
      const { data } = await api.post("/admin/affiliates/parse-amazon", { url: amzUrl.trim() });
      const dupMsg = data.duplicate
        ? `⚠️ This ASIN is already saved as "${data.duplicate_title}". Creating it again will make a duplicate. `
        : "";
      setAmzOpen(false);
      setModal({
        kind: "rec", editing: null,
        form: { ...REC_EMPTY, source: "amazon", retailer: "Amazon", asin: data.asin, affiliate_url: data.affiliate_url, is_must_have: true },
        amazonNote: dupMsg + (data.message || ""),
        paapiAvailable: data.paapi_available,
      });
      setAmzUrl("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAmzImporting(false);
    }
  };

  const endpoints = {
    rec: "/admin/affiliates",
    guide: "/admin/affiliate-guides",
    retailer: "/admin/affiliate-retailers",
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { kind, editing, form } = modal;
    const payload = { ...form };
    if ("sort_order" in payload) payload.sort_order = Number(payload.sort_order) || 0;
    try {
      if (editing) await api.put(`${endpoints[kind]}/${editing.id}`, payload);
      else await api.post(endpoints[kind], payload);
      toast.success(editing ? "Saved" : "Created");
      setModal(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (kind, item, label) => {
    if (!window.confirm(`Delete "${label}"?`)) return;
    try {
      await api.delete(`${endpoints[kind]}/${item.id}`);
      toast.success("Deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setStatus = async (kind, item, status) => {
    try {
      await api.put(`${endpoints[kind]}/${item.id}`, { status });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleRetailer = async (rt) => {
    try {
      await api.put(`/admin/affiliate-retailers/${rt.id}`, { active: !rt.active });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const StatusBadge = ({ s }) => (
    <span className={`text-xs font-bold rounded-full px-2 py-1 ${s === "published" ? "bg-green-100 text-green-700" : s === "archived" ? "bg-navy/10 text-navy/60" : "bg-amber-100 text-amber-700"}`}>{s}</span>
  );

  return (
    <div data-testid="admin-affiliates">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl text-navy">Shop Affiliates</h1>
          <p className="text-ink/60 mt-1">Johnny's Must-Buys — recommended products on outside retailers. Fully separate from the store &amp; Gateway.</p>
        </div>
      </div>

      {stats && (
        <div className="mt-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Published", value: stats.published },
              { label: "Drafts", value: stats.drafts },
              { label: "Total Clicks", value: stats.total_clicks },
              { label: "Active Retailers", value: stats.retailers_active },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-lg border border-navy/15 p-4">
                <div className="text-2xl font-display text-navy">{s.value}</div>
                <div className="text-xs text-ink/60 uppercase font-bold">{s.label}</div>
              </div>
            ))}
          </div>
          <div data-testid="amazon-api-status" className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm border ${stats.amazon_api_ready ? "bg-green-50 border-green-300 text-green-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}>
            <Sparkles className="w-4 h-4 shrink-0 mt-0.5" />
            <span><b>Amazon API:</b> {stats.amazon_api_ready ? "Connected" : "Not connected"} — {stats.amazon_api_note}</span>
          </div>
        </div>
      )}

      <div className="mt-6 flex gap-2 border-b border-navy/15">
        {[
          { k: "recs", label: "Recommendations", icon: ShoppingBag },
          { k: "retailers", label: "Retailers", icon: Store },
          { k: "guides", label: "Buying Guides", icon: BookOpen },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`affiliate-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-3 font-bold text-sm border-b-2 -mb-px transition-colors ${tab === t.k ? "border-brick text-brick" : "border-transparent text-navy/60 hover:text-navy"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* RECOMMENDATIONS */}
      {tab === "recs" && (
        <div className="mt-5">
          <div className="flex flex-col sm:flex-row gap-3">
            <button data-testid="add-amazon-btn" onClick={() => { setAmzOpen(true); setAmzUrl(""); }} className="inline-flex items-center justify-center gap-2 bg-[#ff9900] text-navy font-bold uppercase rounded-md px-5 py-3.5 hover:brightness-95 transition">
              <Sparkles className="w-4 h-4" /> Add Amazon Product
            </button>
            <button data-testid="add-affiliate-btn" onClick={() => openRec(null)} className="inline-flex items-center justify-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3.5 hover:bg-navy transition-colors">
              <Plus className="w-4 h-4" /> Add Manually
            </button>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {recs.map((r) => (
              <div key={r.id} data-testid={`admin-affiliate-${r.id}`} className="bg-white rounded-lg border border-navy/15 p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <StatusBadge s={r.status} />
                    <h3 className="mt-2 font-bold text-navy">{r.title}</h3>
                    <p className="text-xs text-ink/60">{r.retailer || "no retailer"} · {r.category || "uncategorized"}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`edit-affiliate-${r.id}`} onClick={() => openRec(r)} className="p-2 text-navy hover:text-brick"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-affiliate-${r.id}`} onClick={() => remove("rec", r, r.title)} className="p-2 text-navy hover:text-brick"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {r.recommendation && <p className="mt-2 text-sm text-ink/75">{r.recommendation}</p>}
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="inline-flex items-center gap-1 text-ink/50"><MousePointerClick className="w-3.5 h-3.5" /> {r.clicks || 0} clicks</span>
                  <a href={r.affiliate_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brick font-bold">link <ExternalLink className="w-3 h-3" /></a>
                </div>
                <div className="mt-3 flex gap-2">
                  {r.status !== "published" && <button data-testid={`publish-affiliate-${r.id}`} onClick={() => setStatus("rec", r, "published")} className="text-xs font-bold text-green-700 border border-green-300 rounded px-2 py-1">Publish</button>}
                  {r.status !== "draft" && <button onClick={() => setStatus("rec", r, "draft")} className="text-xs font-bold text-amber-700 border border-amber-300 rounded px-2 py-1">Unpublish</button>}
                  {r.status !== "archived" && <button onClick={() => setStatus("rec", r, "archived")} className="text-xs font-bold text-navy/60 border border-navy/20 rounded px-2 py-1">Archive</button>}
                </div>
              </div>
            ))}
            {recs.length === 0 && <p className="text-navy/50">No recommendations yet.</p>}
          </div>
        </div>
      )}

      {/* RETAILERS */}
      {tab === "retailers" && (
        <div className="mt-5">
          <button data-testid="add-retailer-btn" onClick={() => openRetailer(null)} className="inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3 hover:bg-navy transition-colors">
            <Plus className="w-4 h-4" /> Add Retailer
          </button>
          <p className="text-xs text-ink/60 mt-2">Retailers are <b>inactive by default</b>. Activate the ones you've joined.</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {retailers.map((rt) => (
              <div key={rt.id} data-testid={`admin-retailer-${rt.id}`} className="bg-white rounded-lg border border-navy/15 p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-navy">{rt.name}</div>
                  {rt.website && <a href={rt.website} target="_blank" rel="noreferrer" className="text-xs text-brick">{rt.website}</a>}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`toggle-retailer-${rt.id}`} onClick={() => toggleRetailer(rt)} className={`text-xs font-bold rounded-full px-3 py-1.5 ${rt.active ? "bg-green-100 text-green-700" : "bg-navy/10 text-navy/60"}`}>
                    {rt.active ? "Active" : "Inactive"}
                  </button>
                  <button onClick={() => openRetailer(rt)} className="p-2 text-navy hover:text-brick"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove("retailer", rt, rt.name)} className="p-2 text-navy hover:text-brick"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* GUIDES */}
      {tab === "guides" && (
        <div className="mt-5">
          <button data-testid="add-guide-btn" onClick={() => openGuide(null)} className="inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3 hover:bg-navy transition-colors">
            <Plus className="w-4 h-4" /> Add Guide
          </button>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {guides.map((g) => (
              <div key={g.id} data-testid={`admin-guide-${g.id}`} className="bg-white rounded-lg border border-navy/15 p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <StatusBadge s={g.status} />
                    <h3 className="mt-2 font-bold text-navy">{g.title}</h3>
                    {g.summary && <p className="text-xs text-ink/60 mt-1">{g.summary}</p>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => openGuide(g)} className="p-2 text-navy hover:text-brick"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove("guide", g, g.title)} className="p-2 text-navy hover:text-brick"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  {g.status !== "published" && <button onClick={() => setStatus("guide", g, "published")} className="text-xs font-bold text-green-700 border border-green-300 rounded px-2 py-1">Publish</button>}
                  {g.status === "published" && <button onClick={() => setStatus("guide", g, "draft")} className="text-xs font-bold text-amber-700 border border-amber-300 rounded px-2 py-1">Unpublish</button>}
                </div>
              </div>
            ))}
            {guides.length === 0 && <p className="text-navy/50">No guides yet.</p>}
          </div>
        </div>
      )}

      {/* MODAL */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setModal(null)}>
          <form data-testid="affiliate-form" onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg w-full max-w-lg p-6 my-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-navy">{modal.editing ? "Edit" : "Add"} {modal.kind === "rec" ? "Recommendation" : modal.kind === "guide" ? "Guide" : "Retailer"}</h2>
              <button type="button" onClick={() => setModal(null)} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
            </div>

            {modal.kind === "rec" && (
              <>
                {modal.amazonNote && (
                  <div data-testid="amazon-import-note" className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{modal.amazonNote}</span>
                  </div>
                )}
                {modal.form.asin && (
                  <div className="mt-3 text-xs text-ink/60">ASIN <span className="font-mono font-bold text-navy">{modal.form.asin}</span> · tag preserved: <span className="font-mono">johnnymart-20</span></div>
                )}
                <Field label="Title"><input data-testid="af-title" required value={modal.form.title} onChange={(e) => setModal({ ...modal, form: { ...modal.form, title: e.target.value } })} className={inputCls} /></Field>
                <Field label="Affiliate URL"><input data-testid="af-url" required value={modal.form.affiliate_url} onChange={(e) => setModal({ ...modal, form: { ...modal.form, affiliate_url: e.target.value } })} placeholder="https://..." className={inputCls} /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Retailer">
                    <select data-testid="af-retailer" value={modal.form.retailer} onChange={(e) => setModal({ ...modal, form: { ...modal.form, retailer: e.target.value } })} className={`${inputCls} bg-white`}>
                      <option value="">— Select —</option>
                      <option value="Amazon">Amazon</option>
                      {retailers.filter((rt) => rt.name !== "Amazon").map((rt) => <option key={rt.id} value={rt.name}>{rt.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Category"><input value={modal.form.category} onChange={(e) => setModal({ ...modal, form: { ...modal.form, category: e.target.value } })} className={inputCls} /></Field>
                </div>
                <Field label="Product image URL — paste any image link"><input data-testid="af-image" value={modal.form.image_url} onChange={(e) => setModal({ ...modal, form: { ...modal.form, image_url: e.target.value } })} placeholder="https://… (right-click the product photo → Copy Image Address)" className={inputCls} /></Field>
                {(modal.form.image_url || modal.form.amazon_image_link) && (
                  <div className="mt-2 flex items-center gap-3">
                    <div className="w-24 h-24 rounded-md border border-navy/15 bg-white flex items-center justify-center overflow-hidden shrink-0">
                      <img
                        data-testid="af-image-preview"
                        src={modal.form.amazon_image_link || modal.form.image_url}
                        alt="preview"
                        referrerPolicy="no-referrer"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
                        onLoad={(e) => { e.currentTarget.style.display = "block"; e.currentTarget.nextSibling.style.display = "none"; }}
                      />
                      <span style={{ display: "none" }} className="text-[10px] text-brick font-bold px-1 text-center">Image won't load — check the URL</span>
                    </div>
                    <p className="text-[11px] text-ink/55">Live preview of the image shoppers will see. If it's blank, the link is wrong or the site blocks hotlinking — try the "Copy Image Address" URL.</p>
                  </div>
                )}
                <Field label="Amazon Product Link code (official image)">
                  <textarea data-testid="af-amazon-image" rows={2} value={modal.form.amazon_image_link} onChange={(e) => setModal({ ...modal, form: { ...modal.form, amazon_image_link: e.target.value } })} placeholder="Paste the Product Links image/iframe code from Associates Central → Product Links" className={`${inputCls} resize-none font-mono text-xs`} />
                  <p className="text-[11px] text-ink/55 mt-1">Compliant image method: generate a Product Link in Amazon Associates Central and paste it here. We only extract Amazon's official image link — we never download or store the image. If left blank, a clean "View on Amazon" placeholder is shown.</p>
                </Field>
                <Field label="Short description / Johnny says"><textarea data-testid="af-recommendation" rows={3} value={modal.form.recommendation} onChange={(e) => setModal({ ...modal, form: { ...modal.form, recommendation: e.target.value } })} className={`${inputCls} resize-none`} /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Price note"><input value={modal.form.price_note} onChange={(e) => setModal({ ...modal, form: { ...modal.form, price_note: e.target.value } })} placeholder="e.g. ~$25" className={inputCls} /></Field>
                  <Field label="Status">
                    <select data-testid="af-status" value={modal.form.status} onChange={(e) => setModal({ ...modal, form: { ...modal.form, status: e.target.value } })} className={`${inputCls} bg-white`}>
                      <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
                    </select>
                  </Field>
                </div>
                <label className="flex items-center gap-2 mt-4 text-sm font-semibold text-navy">
                  <input data-testid="af-musthave" type="checkbox" checked={!!modal.form.is_must_have} onChange={(e) => setModal({ ...modal, form: { ...modal.form, is_must_have: e.target.checked } })} />
                  <Star className="w-4 h-4 fill-gold text-gold" /> Feature in "Johnny's Must Haves"
                </label>
              </>
            )}

            {modal.kind === "guide" && (
              <>
                <Field label="Title"><input data-testid="gf-title" required value={modal.form.title} onChange={(e) => setModal({ ...modal, form: { ...modal.form, title: e.target.value } })} className={inputCls} /></Field>
                <Field label="Summary"><input value={modal.form.summary} onChange={(e) => setModal({ ...modal, form: { ...modal.form, summary: e.target.value } })} className={inputCls} /></Field>
                <Field label="Body"><textarea data-testid="gf-body" rows={6} value={modal.form.body} onChange={(e) => setModal({ ...modal, form: { ...modal.form, body: e.target.value } })} className={`${inputCls} resize-none`} /></Field>
                <Field label="Status">
                  <select value={modal.form.status} onChange={(e) => setModal({ ...modal, form: { ...modal.form, status: e.target.value } })} className={`${inputCls} bg-white`}>
                    <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
                  </select>
                </Field>
              </>
            )}

            {modal.kind === "retailer" && (
              <>
                <Field label="Name"><input data-testid="rt-name" required value={modal.form.name} onChange={(e) => setModal({ ...modal, form: { ...modal.form, name: e.target.value } })} className={inputCls} /></Field>
                <Field label="Website"><input value={modal.form.website} onChange={(e) => setModal({ ...modal, form: { ...modal.form, website: e.target.value } })} placeholder="https://..." className={inputCls} /></Field>
                <Field label="Notes"><input value={modal.form.notes} onChange={(e) => setModal({ ...modal, form: { ...modal.form, notes: e.target.value } })} className={inputCls} /></Field>
                <label className="flex items-center gap-2 mt-4 text-sm font-semibold text-navy">
                  <input data-testid="rt-active" type="checkbox" checked={modal.form.active} onChange={(e) => setModal({ ...modal, form: { ...modal.form, active: e.target.checked } })} /> Active (you've joined this program)
                </label>
              </>
            )}

            <button data-testid="save-affiliate-btn" disabled={saving} className="mt-6 w-full bg-brick text-white font-bold uppercase rounded-md py-3 hover:bg-navy transition-colors disabled:opacity-60">
              {saving ? "Saving..." : modal.editing ? "Save Changes" : "Create"}
            </button>
          </form>
        </div>
      )}

      {/* AMAZON IMPORT MODAL */}
      {amzOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAmzOpen(false)}>
          <div data-testid="amazon-import-modal" onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-navy flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#ff9900]" /> Add Amazon Product</h2>
              <button type="button" onClick={() => setAmzOpen(false)} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
            </div>
            <p className="mt-2 text-sm text-ink/65">Paste your Amazon Associates link. We'll extract the ASIN and keep your <span className="font-mono">johnnymart-20</span> tag.</p>
            <div className="mt-4 flex gap-2">
              <input data-testid="amazon-url-input" value={amzUrl} onChange={(e) => setAmzUrl(e.target.value)} placeholder="https://www.amazon.com/dp/... or https://amzn.to/..." className={`${inputCls} mt-0 text-base`} />
            </div>
            <button data-testid="paste-clipboard-btn" type="button" onClick={pasteClipboard} className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-navy border border-navy/20 rounded-md px-3 py-2 hover:border-brick">
              <ClipboardPaste className="w-4 h-4" /> Paste from Clipboard
            </button>
            <button data-testid="import-amazon-btn" disabled={amzImporting || !amzUrl.trim()} onClick={importAmazon} className="mt-4 w-full bg-[#ff9900] text-navy font-bold uppercase rounded-md py-3.5 hover:brightness-95 transition disabled:opacity-60">
              {amzImporting ? "Importing..." : "Import Product"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
